'use client';

import { useState, useMemo, useCallback } from 'react';
import { calculatorConfigs } from './pricing-data';
import type { CalculatorResult } from './types';

function formatCurrency(amount: number): string {
  if (amount === 0) return '$0';
  if (amount < 0.01) return '< $0.01';
  if (amount >= 1000) return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `$${amount.toFixed(2)}`;
}

function RankBadge({ rank }: { rank: number }) {
  const labels = ['1st', '2nd', '3rd', '4th'];
  const colors = [
    'bg-green-100 text-green-800 border-green-200',
    'bg-gray-100 text-gray-700 border-gray-200',
    'bg-gray-100 text-gray-700 border-gray-200',
    'bg-gray-100 text-gray-600 border-gray-200',
  ];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${colors[rank - 1] || colors[3]}`}>
      {labels[rank - 1] || `${rank}th`}
    </span>
  );
}

function SliderInput({
  id,
  label,
  unit,
  min,
  max,
  step,
  value,
  onChange,
}: {
  id: string;
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (id: string, value: number) => void;
}) {
  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(id, Number(e.target.value));
    },
    [id, onChange]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value === '' ? min : Number(e.target.value);
      if (!isNaN(val)) {
        onChange(id, Math.max(min, Math.min(max, val)));
      }
    },
    [id, min, max, onChange]
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label htmlFor={`slider-${id}`} className="text-sm font-medium text-gray-700">
          {label}
        </label>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            id={`number-${id}`}
            aria-label={`${label} (${unit})`}
            value={value}
            onChange={handleInputChange}
            min={min}
            max={max}
            step={step}
            className="w-24 px-2 py-1 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
          <span className="text-xs text-gray-600 w-16">{unit}</span>
        </div>
      </div>
      <input
        type="range"
        id={`slider-${id}`}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleSliderChange}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
      />
    </div>
  );
}

function SelectInput({
  id,
  label,
  options,
  value,
  onChange,
}: {
  id: string;
  label: string;
  options: { label: string; value: number }[];
  value: number;
  onChange: (id: string, value: number) => void;
}) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(id, Number(e.target.value));
    },
    [id, onChange]
  );

  return (
    <div className="space-y-2">
      <label htmlFor={`select-${id}`} className="text-sm font-medium text-gray-700">
        {label}
      </label>
      <select
        id={`select-${id}`}
        value={value}
        onChange={handleChange}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

export default function CloudProviderCalculator({ type }: { type: string }) {
  const config = calculatorConfigs[type];

  const [inputs, setInputs] = useState<Record<string, number>>(() => {
    if (!config) return {};
    const defaults: Record<string, number> = {};
    for (const input of config.inputs) {
      defaults[input.id] = input.defaultValue;
    }
    return defaults;
  });

  const handleInputChange = useCallback((id: string, value: number) => {
    setInputs(prev => ({ ...prev, [id]: value }));
  }, []);

  const results: CalculatorResult[] = useMemo(() => {
    if (!config) return [];

    const calculated = config.providers.map(provider => ({
      name: provider.name,
      color: provider.color,
      monthlyCost: provider.calculate(inputs),
      annualCost: provider.calculate(inputs) * 12,
      note: provider.notes?.(inputs),
      rank: 0,
    }));

    // Sort by monthly cost and assign ranks
    calculated.sort((a, b) => a.monthlyCost - b.monthlyCost);
    calculated.forEach((result, i) => {
      result.rank = i + 1;
    });

    return calculated;
  }, [config, inputs]);

  if (!config) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 my-8 text-red-700">
        Unknown calculator type: {type}
      </div>
    );
  }

  const maxCost = Math.max(...results.map(r => r.monthlyCost), 1);

  return (
    <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 my-8 not-prose">
      <h3 className="text-lg font-bold text-gray-900 mb-1">{config.title}</h3>
      <p className="text-sm text-gray-600 mb-5">{config.description}</p>

      {/* Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {config.inputs.map(input =>
          input.options ? (
            <div key={input.id} className="sm:col-span-2">
              <SelectInput
                id={input.id}
                label={input.label}
                options={input.options}
                value={inputs[input.id] ?? input.defaultValue}
                onChange={handleInputChange}
              />
            </div>
          ) : (
            <SliderInput
              key={input.id}
              id={input.id}
              label={input.label}
              unit={input.unit}
              min={input.min}
              max={input.max}
              step={input.step}
              value={inputs[input.id] ?? input.defaultValue}
              onChange={handleInputChange}
            />
          )
        )}
      </div>

      {/* Results */}
      <div className="space-y-3">
        {results.map(result => {
          const barWidth = maxCost > 0 ? Math.max(2, (result.monthlyCost / maxCost) * 100) : 2;
          const isZero = result.monthlyCost === 0;

          return (
            <div
              key={result.name}
              className={`bg-white rounded-lg border p-4 ${
                result.rank === 1 ? 'border-green-300 ring-1 ring-green-100' : 'border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: result.color }}
                  />
                  <span className="font-semibold text-gray-900 text-sm">{result.name}</span>
                  <RankBadge rank={result.rank} />
                </div>
                <div className="text-right">
                  <span className="font-bold text-gray-900">{formatCurrency(result.monthlyCost)}</span>
                  <span className="text-xs text-gray-500 ml-1">/mo</span>
                </div>
              </div>

              {/* Cost bar */}
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: isZero ? '2%' : `${barWidth}%`,
                    backgroundColor: result.color,
                    opacity: isZero ? 0.3 : 0.7,
                  }}
                />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">
                  {formatCurrency(result.annualCost)}/year
                </span>
                {result.note && (
                  <span className="text-xs text-gray-600 text-right max-w-[70%]">{result.note}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-600 mt-4 text-center">
        Estimates based on published pricing as of February 2026. Actual costs may vary by region, commitment, and usage patterns.
      </p>
    </div>
  );
}
